import {
  customElement,
  DeesElement,
  property,
  state,
  html,
  cssManager,
  css,
  type TemplateResult,
  domtools,
} from '@design.estate/dees-element';
import '@design.estate/dees-catalog';
import './dotglobal-file-view.js';
import './dotglobal-repo-home.js';
import './dotglobal-propose-modal.js';
import './dotglobal-agent-view.js';
import './dotglobal-activity-panel.js';
import './dotglobal-change-badge.js';
import './dotglobal-sign-in.js';
import './dotglobal-settings.js';
import './dotglobal-forges.js';
import './dotglobal-user-profile.js';
import './dotglobal-releases.js';
import './dotglobal-issues.js';
import './dotglobal-pulls.js';
import './dotglobal-activity-digest.js';
import './dotglobal-explore.js';
import './dotglobal-all-orgs.js';
import './dotglobal-org-view.js';
import './dotglobal-packages.js';
import './dotglobal-commits.js';
import './dotglobal-commit-detail.js';
import { renderMarkdown as renderMarkdownShared } from './dotglobal-markdown.js';
import { languageColor } from './dotglobal-language-colors.js';
import { describeAuthError } from './dotglobal-auth-errors.js';

import type * as interfaces from '../../ts/ffb.interfaces.js';

type TRouteView = 'overview' | 'orgs' | 'org' | 'packages' | 'repo' | 'file' | 'agent' | 'signin' | 'settings' | 'connections' | 'profile';
type TRepoTab = 'code' | 'commits' | 'issues' | 'pulls' | 'discussions' | 'releases';

interface IRepoInteractionState {
  starred?: boolean;
  watching?: boolean;
}

interface IAuthState {
  userId: string;
  user: string;
  displayName: string;
  csrfToken: string;
  absoluteExpiresAt: string;
}

interface IRouteState {
  view: TRouteView;
  org?: string;
  repo?: string;
  path?: string;
  tab?: TRepoTab;
  query?: string;
  profileUser?: string;
  packageKind?: interfaces.TPackageKind;
  packageOwner?: string;
  packageName?: string;
  authError?: string;
  returnTo?: string;
}


const getInitialActivityPanelState = (): boolean => {
  const storedPreference = globalThis.localStorage?.getItem('fg.activityPanel');
  if (storedPreference) {
    return storedPreference !== '0';
  }
  return (globalThis.innerWidth || 1200) > 1100;
};

const readStoredTheme = (): 'dark' | 'light' => {
  try {
    const storage = globalThis.localStorage;
    // One-time migration to the Apple light default; respects toggles afterward.
    if (storage && !storage.getItem('fg.theme.appleMigrated')) {
      storage.setItem('fg.theme', 'light');
      storage.setItem('fg.theme.appleMigrated', '1');
    }
    return storage?.getItem('fg.theme') === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
};

@customElement('dotglobal-app')
export class DotglobalApp extends DeesElement {
  @property({ type: String, reflect: true })
  public accessor theme: 'dark' | 'light' = readStoredTheme();

  @state()
  private accessor route: IRouteState = { view: 'overview' };

  @state()
  private accessor searchQuery = '';

  @state()
  private accessor loading = false;

  @state()
  private accessor errorMessage = '';

  @state()
  private accessor overviewData: interfaces.IReq_PublicOverview['response'] | null = null;

  @state()
  private accessor orgData: interfaces.IReq_PublicOrg['response'] | null = null;

  @state()
  private accessor repoData: interfaces.IReq_PublicRepo['response'] | null = null;

  @state()
  private accessor fileData: interfaces.IReq_PublicFile['response'] | null = null;

  @state()
  private accessor commitsData: interfaces.IReq_PublicCommits['response'] | null = null;

  @state()
  private accessor activityPanelOpen = getInitialActivityPanelState();

  @state()
  private accessor toastMessage = '';

  @state()
  private accessor proposeModalOpen = false;

  @state()
  private accessor repoInteractions: Record<string, IRepoInteractionState> = {};

  @state()
  private accessor auth: IAuthState | null = null;

  @state()
  private accessor authLoading = true;

  @state()
  private accessor authAvailable = false;

  @state()
  private accessor userMenuOpen = false;

  private activeLoadId = 0;
  private overviewDataKey = '';
  private orgDataKey = '';
  private repoDataKey = '';
  private fileDataKey = '';
  private commitsDataKey = '';
  private authRefreshId = 0;
  private authExpiryTimer?: ReturnType<typeof setTimeout>;
  private authRetryTimer?: ReturnType<typeof setTimeout>;

  private handleHashChange = () => {
    const nextRoute = this.parseHashRoute();
    this.route = nextRoute;
    this.searchQuery = this.route.query ?? '';
    void this.loadRouteData();
    if (this.authAvailable || this.auth) {
      void this.refreshSession();
    }
  };

  private handleWindowFocus = () => {
    void this.refreshSession();
  };

  public static styles = [
    cssManager.defaultStyles,
    css`
      /* ============================================================
         Apple HIG token layer (foss.global v4).
         Reskinned over the original short variable names so every child
         component inherits the new look through the cascade without any
         markup changes. Mirrors the design's styles/tokens.css.
         ============================================================ */
      :host {
        /* fonts — San Francisco system stack */
        --font-sans: -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'SF Pro Display', 'Helvetica Neue', Helvetica, Arial, sans-serif;
        --font-display: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Helvetica Neue', Helvetica, sans-serif;
        --font-mono: ui-monospace, 'SF Mono', 'SFMono-Regular', Menlo, Monaco, 'Cascadia Code', 'Liberation Mono', monospace;

        /* accent / semantic (system colors, light) */
        --accent: #007aff;
        --accent-fg: #ffffff;

        /* backgrounds (system / grouped surfaces) */
        --bg-primary: #ffffff;
        --bg-secondary: #f5f5f7;
        --bg-tertiary: #eeeef0;

        /* text (label colors) */
        --text-primary: #1d1d1f;
        --text-secondary: #515154;
        --text-muted: #86868b;

        /* borders (separators / hairlines) */
        --border-default: #d2d2d7;
        --border-subtle: #e8e8ed;
        --border-strong: #c3c3c9;

        /* semantic */
        --success: #34c759;
        --warning: #ff9500;
        --error: #ff3b30;

        /* interactive states */
        --hover: rgba(0, 0, 0, 0.045);
        --active: rgba(0, 0, 0, 0.07);
        --row-hover: rgba(0, 122, 255, 0.06);

        --code-bg: #f5f5f7;

        /* elevation — soft, diffuse */
        --shadow-xs: 0 1px 2px rgba(0, 0, 0, 0.05);
        --shadow-sm: 0 2px 8px rgba(0, 0, 0, 0.07);
        --shadow-md: 0 6px 22px rgba(0, 0, 0, 0.1);
        --shadow-lg: 0 14px 44px rgba(0, 0, 0, 0.16);

        --nav-h: 52px;
        --maxw: 1180px;
        display: block;
        min-height: 100vh;
        font-family: var(--font-sans);
        font-size: 14px;
        line-height: 1.5;
        color: var(--text-primary);
        background: var(--bg-primary);
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        font-feature-settings: 'tnum';
      }

      :host([theme='dark']) {
        --bg-primary: #1c1c1e;
        --bg-secondary: #141416;
        --bg-tertiary: #2c2c2e;
        --text-primary: #f5f5f7;
        --text-secondary: #aeaeb2;
        --text-muted: #8e8e93;
        --border-default: rgba(255, 255, 255, 0.12);
        --border-subtle: rgba(255, 255, 255, 0.07);
        --border-strong: rgba(255, 255, 255, 0.2);
        --accent: #0a84ff;
        --success: #30d158;
        --warning: #ff9f0a;
        --error: #ff453a;
        --hover: rgba(255, 255, 255, 0.07);
        --active: rgba(255, 255, 255, 0.1);
        --row-hover: rgba(10, 132, 255, 0.13);
        --code-bg: #2c2c2e;
        --shadow-xs: 0 1px 2px rgba(0, 0, 0, 0.4);
        --shadow-sm: 0 2px 10px rgba(0, 0, 0, 0.5);
        --shadow-md: 0 6px 24px rgba(0, 0, 0, 0.6);
        --shadow-lg: 0 16px 48px rgba(0, 0, 0, 0.7);
      }

      * {
        box-sizing: border-box;
      }

      a {
        color: inherit;
        text-decoration: none;
      }

      button,
      input {
        font: inherit;
      }

      button {
        border: 0;
        background: none;
        color: inherit;
        cursor: pointer;
      }

      dees-icon {
        display: inline-flex;
        flex-shrink: 0;
      }

      .app {
        min-height: 100vh;
        display: flex;
        flex-direction: column;
        background: var(--bg-primary);
      }

      .page {
        width: 100%;
        max-width: var(--maxw);
        margin: 0 auto;
        padding: 0 24px;
      }

      .page-wide {
        width: 100%;
        margin: 0 auto;
        padding: 0 24px;
      }

      .grow {
        flex: 1;
      }

      .stack {
        display: flex;
        flex-direction: column;
      }

      .topnav {
        position: sticky;
        top: 0;
        /* above the activity side-panel (z-index 90 on narrow viewports) so the
           user-menu dropdown stays clickable; modals (100) / toasts (200) remain above */
        z-index: 95;
        height: var(--nav-h);
        border-bottom: 1px solid var(--border-subtle);
        background: color-mix(in srgb, var(--bg-primary) 72%, transparent);
        backdrop-filter: saturate(180%) blur(20px);
        -webkit-backdrop-filter: saturate(180%) blur(20px);
      }

      .topnav .inner {
        height: 100%;
        display: flex;
        align-items: center;
        gap: 16px;
      }

      .brand {
        display: inline-flex;
        align-items: center;
        gap: 9px;
        min-width: max-content;
        cursor: pointer;
        user-select: none;
      }

      .brandmark,
      .logo {
        width: 26px;
        height: 26px;
        /* The brand logomark reads in the label color in the top nav (matching the
           wordmark); only the footer logo is accent-tinted. */
        color: var(--text-primary);
        flex-shrink: 0;
      }

      .footer .logo {
        color: var(--accent);
      }

      .brandname {
        font-family: var(--font-display);
        font-size: 16px;
        font-weight: 700;
        letter-spacing: -0.02em;
      }

      .brandname span {
        color: var(--text-muted);
        font-weight: 600;
      }

      .navsearch {
        position: relative;
        flex: 1;
        max-width: 460px;
      }

      .navsearch input {
        width: 100%;
        height: 34px;
        padding: 0 42px 0 34px;
        color: var(--text-primary);
        background: var(--bg-tertiary);
        border: 1px solid var(--border-default);
        border-radius: 6px;
        font-size: 13px;
        outline: none;
        transition: border-color 0.15s, box-shadow 0.15s;
      }

      .navsearch input:focus {
        border-color: var(--accent);
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 18%, transparent);
      }

      .navsearch dees-icon {
        position: absolute;
        top: 9px;
        left: 11px;
        color: var(--text-muted);
      }

      .kbd {
        position: absolute;
        top: 8px;
        right: 9px;
        padding: 1px 6px;
        border: 1px solid var(--border-default);
        border-radius: 5px;
        color: var(--text-muted);
        font-family: var(--font-mono);
        font-size: 11px;
      }

      .navlink,
      .iconbtn,
      .btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 7px;
        border-radius: 6px;
        white-space: nowrap;
      }

      .navlink {
        height: 34px;
        padding: 0 8px;
        color: var(--text-secondary);
        font-size: 13px;
        font-weight: 500;
        transition: color 0.15s, background 0.15s;
      }

      .navlink:hover,
      .navlink.on,
      .iconbtn:hover {
        color: var(--text-primary);
        background: var(--hover);
      }

      .navlink.on {
        color: var(--accent);
        background: color-mix(in srgb, var(--accent) 10%, transparent);
      }

      .iconbtn {
        width: 34px;
        height: 34px;
        color: var(--text-secondary);
      }

      .usermenu-wrap {
        position: relative;
        display: flex;
        margin-left: 4px;
      }

      .avatarbtn {
        display: flex;
        padding: 0;
        border: 0;
        background: none;
        border-radius: 50%;
        cursor: pointer;
      }

      .menu-backdrop {
        position: fixed;
        inset: 0;
        z-index: 59;
      }

      .pop {
        position: absolute;
        z-index: 60;
        background: var(--bg-primary);
        border: 1px solid var(--border-default);
        border-radius: 8px;
        box-shadow: var(--shadow-lg);
      }

      .usermenu {
        top: 40px;
        right: 0;
        width: 224px;
        padding: 6px;
      }

      .um-head {
        display: flex;
        align-items: center;
        gap: 10px;
        width: 100%;
        padding: 8px;
        border-radius: 6px;
        cursor: pointer;
        text-align: left;
      }

      .um-head:hover {
        background: var(--hover);
      }

      .um-item {
        display: flex;
        align-items: center;
        gap: 10px;
        width: 100%;
        padding: 7px 9px;
        border-radius: 6px;
        color: var(--text-secondary);
        font-size: 13px;
        font-weight: 500;
        text-align: left;
        cursor: pointer;
      }

      .um-item:hover {
        background: var(--hover);
        color: var(--text-primary);
      }

      .um-sep {
        height: 1px;
        margin: 5px 4px;
        background: var(--border-default);
      }

      .activitydot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: var(--success);
        flex-shrink: 0;
      }

      @media (prefers-reduced-motion: no-preference) {
        .activitydot {
          animation: activitypulse 2.4s ease-out infinite;
        }
      }

      @keyframes activitypulse {
        0% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.45); }
        70% { box-shadow: 0 0 0 7px rgba(34, 197, 94, 0); }
        100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); }
      }

      .appshell {
        display: flex;
        align-items: stretch;
        flex: 1;
        min-width: 0;
      }

      .appmain {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
      }

      .content {
        flex: 1;
        padding-top: 24px;
        padding-bottom: 56px;
      }

      .hero {
        padding-top: 44px;
        padding-bottom: 28px;
      }

      .hero h1 {
        margin: 0 0 12px;
        font-family: var(--font-display);
        font-size: 40px;
        font-weight: 800;
        line-height: 1.05;
        letter-spacing: -0.03em;
      }

      .hero p {
        max-width: 560px;
        margin: 0;
        color: var(--text-secondary);
        font-size: 16px;
        line-height: 1.55;
      }

      .hero-actions,
      .row,
      .repo-actions,
      .tabs,
      .chips,
      .repo-meta,
      .statsrow {
        display: flex;
        align-items: center;
        gap: 10px;
        flex-wrap: wrap;
      }

      .hero-actions {
        margin-top: 22px;
      }

      .btn {
        height: 34px;
        padding: 0 14px;
        border: 1px solid var(--border-strong);
        color: var(--text-primary);
        background: var(--bg-tertiary);
        font-size: 13px;
        font-weight: 500;
        letter-spacing: -0.01em;
        transition: background 0.15s, border-color 0.15s, color 0.15s, box-shadow 0.15s;
      }

      .btn:hover {
        background: var(--hover);
        border-color: var(--border-strong);
      }

      .btn.primary {
        border-color: var(--accent);
        background: var(--accent);
        color: var(--accent-fg);
      }

      .btn.ghost {
        border-color: transparent;
        background: transparent;
        color: var(--text-secondary);
      }

      .btn.sm {
        height: 30px;
        padding: 0 11px;
        font-size: 12px;
        gap: 6px;
      }

      .btn.lg {
        height: 40px;
        padding: 0 18px;
        font-size: 14px;
      }

      .statbtn {
        display: inline-flex;
        align-items: stretch;
        overflow: hidden;
        border: 1px solid var(--border-strong);
        border-radius: 6px;
        font-size: 12.5px;
        font-weight: 500;
      }

      .statbtn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .statbtn.on {
        border-color: color-mix(in srgb, var(--accent) 45%, transparent);
      }

      .statbtn .l,
      .statbtn .n {
        display: inline-flex;
        align-items: center;
        height: 30px;
      }

      .statbtn .l {
        gap: 6px;
        padding: 0 10px;
        color: var(--text-secondary);
        background: var(--bg-tertiary);
        transition: background 0.15s, color 0.15s;
      }

      .statbtn:not(:disabled) .l:hover,
      .statbtn.on .l {
        color: var(--text-primary);
        background: var(--hover);
      }

      .statbtn.on .l {
        color: var(--accent);
        background: color-mix(in srgb, var(--accent) 14%, transparent);
      }

      .statbtn .n {
        padding: 0 10px;
        border-left: 1px solid var(--border-strong);
        font-family: var(--font-mono);
      }

      .pop {
        position: absolute;
        top: 40px;
        right: 0;
        z-index: 60;
        width: min(372px, calc(100vw - 32px));
        padding: 16px;
        border: 1px solid var(--border-default);
        border-radius: 8px;
        background: var(--bg-primary);
        box-shadow: var(--shadow-lg);
        animation: pop 0.14s ease;
      }

      @keyframes pop {
        from { opacity: 0; transform: translateY(8px) scale(0.99); }
      }

      .copyfield {
        display: flex;
        align-items: center;
        height: 36px;
        overflow: hidden;
        border: 1px solid var(--border-default);
        border-radius: 6px;
        background: var(--bg-tertiary);
      }

      .copyfield .v {
        flex: 1;
        min-width: 0;
        padding: 0 12px;
        overflow: hidden;
        color: var(--text-primary);
        font-family: var(--font-mono);
        font-size: 12.5px;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .copyfield button {
        width: 38px;
        height: 100%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-left: 1px solid var(--border-default);
        color: var(--text-secondary);
      }

      .term {
        overflow: hidden;
        border: 1px solid var(--border-strong);
        border-radius: 6px;
        background: #0a0a0a;
        color: #d4d4d4;
        font-family: var(--font-mono);
        font-size: 12.5px;
        line-height: 1.7;
      }

      .term-h {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 8px 12px;
        border-bottom: 1px solid var(--border-strong);
      }

      .term-dot {
        width: 11px;
        height: 11px;
        border-radius: 50%;
      }

      .term-b {
        padding: 12px 14px;
        white-space: pre-wrap;
      }

      .agent-glyph {
        width: 40px;
        height: 40px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        border-radius: 6px;
        color: #8b5cf6;
        background: color-mix(in srgb, #8b5cf6 18%, transparent);
      }

      .agent-cta {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 16px 18px;
        border: 1px solid color-mix(in srgb, var(--accent) 35%, var(--border-default));
        border-radius: 8px;
        background: color-mix(in srgb, var(--accent) 7%, var(--bg-primary));
      }

      .grid {
        display: grid;
        gap: 14px;
      }

      .grid.repos {
        grid-template-columns: repeat(auto-fill, minmax(330px, 1fr));
      }

      .grid.orgs {
        grid-template-columns: repeat(auto-fill, minmax(290px, 1fr));
      }

      .sectionhead {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: 16px;
        margin: 28px 0 12px;
      }

      .sectionhead h2,
      .repo-title,
      .card-title,
      .digest-title {
        font-family: var(--font-display);
        letter-spacing: -0.02em;
      }

      .sectionhead h2 {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0;
        font-size: 20px;
      }

      .sectionhead p {
        margin: 4px 0 0;
        color: var(--text-muted);
        font-size: 13px;
      }

      .card,
      .repocard,
      .orgtile,
      .listcard,
      .digest,
      .filecard,
      .codecard,
      .profilecard {
        border: 1px solid var(--border-default);
        border-radius: 8px;
        background: var(--bg-primary);
        overflow: hidden;
      }

      .repocard,
      .orgtile {
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding: 18px;
        cursor: pointer;
        transition: border-color 0.15s, background 0.15s;
      }

      .repocard:hover,
      .orgtile:hover {
        border-color: var(--border-strong);
        background: var(--bg-secondary);
      }

      .repo-name,
      .org-name {
        display: flex;
        align-items: center;
        gap: 8px;
        color: var(--accent);
        font-size: 15px;
        font-weight: 600;
      }

      .rc-org:hover {
        text-decoration: underline;
      }

      .orgtile-lang {
        height: 6px;
        display: flex;
        overflow: hidden;
        border-radius: 3px;
        background: var(--bg-tertiary);
      }

      .orgtile-lang span {
        display: block;
      }

      .repo-desc,
      .org-desc,
      .muted {
        color: var(--text-muted);
      }

      .repo-desc,
      .org-desc {
        min-height: 40px;
        margin-top: 8px;
        font-size: 13px;
        line-height: 1.5;
      }

      .repo-meta {
        margin-top: 12px;
        gap: 14px;
        color: var(--text-muted);
        font-size: 12px;
      }

      .meta-item {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }

      .lang-dot {
        width: 11px;
        height: 11px;
        border-radius: 50%;
        background: var(--accent);
      }

      .badge,
      .topic,
      .statpill {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        border-radius: 999px;
        white-space: nowrap;
      }

      .badge {
        padding: 2px 8px;
        border: 1px solid transparent;
        color: var(--text-secondary);
        background: var(--bg-tertiary);
        font-size: 11.5px;
        font-weight: 500;
        line-height: 1.5;
      }

      .badge.sq {
        border-radius: 4px;
      }

      .badge.def {
        color: var(--text-secondary);
        background: var(--bg-tertiary);
      }

      .badge.accent {
        color: var(--accent);
        background: color-mix(in srgb, var(--accent) 16%, transparent);
      }

      .badge.outline {
        border-color: var(--border-strong);
        color: var(--text-secondary);
        background: transparent;
      }

      .badge.success {
        color: #1c7a3f;
        background: #e4f8ea;
      }

      :host([theme='dark']) .badge.success {
        color: #4cd964;
        background: rgba(48, 209, 88, 0.18);
      }

      .topic {
        padding: 2px 8px;
        color: var(--text-secondary);
        border: 1px solid var(--border-default);
        font-size: 11px;
      }

      .statpill {
        height: 34px;
        padding: 0 12px;
        border: 1px solid var(--border-default);
        background: var(--bg-tertiary);
        color: var(--text-secondary);
        font-size: 12px;
      }

      .statpill strong {
        color: var(--text-primary);
        font-family: var(--font-mono);
      }

      .kpi {
        display: inline-flex;
        align-items: baseline;
        gap: 6px;
      }

      .kpi .n {
        font-family: var(--font-mono);
        font-weight: 600;
      }

      .chip {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        height: 30px;
        padding: 0 12px;
        border: 1px solid var(--border-default);
        border-radius: 6px;
        color: var(--text-secondary);
        background: var(--bg-tertiary);
        font-size: 13px;
        font-weight: 500;
        white-space: nowrap;
        cursor: pointer;
        transition: color 0.12s, border-color 0.12s, background 0.12s;
      }

      .chip.on {
        color: var(--accent);
        border-color: color-mix(in srgb, var(--accent) 45%, transparent);
        background: color-mix(in srgb, var(--accent) 14%, transparent);
      }

      .chip:hover {
        color: var(--text-primary);
        border-color: var(--border-strong);
      }

      .seg {
        display: inline-flex;
        padding: 2px;
        border: 1px solid var(--border-default);
        border-radius: 6px;
        background: var(--bg-tertiary);
      }

      .seg button {
        height: 26px;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 0 11px;
        border-radius: 4px;
        color: var(--text-secondary);
        font-size: 12px;
        font-weight: 500;
      }

      .seg button:hover {
        color: var(--text-primary);
      }

      .seg button.on {
        color: var(--text-primary);
        background: var(--bg-primary);
        box-shadow: var(--shadow-xs);
      }

      .seg button:disabled {
        opacity: 0.55;
        cursor: not-allowed;
      }

      .filterbar {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .fpill {
        height: 30px;
        display: inline-flex;
        align-items: center;
        gap: 7px;
        padding: 0 11px;
        border-radius: 6px;
        color: var(--text-secondary);
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
      }

      .fpill:hover {
        color: var(--text-primary);
        background: var(--hover);
      }

      .fpill.on {
        color: var(--text-primary);
      }

      .orgtile {
        min-height: 152px;
        display: flex;
        flex-direction: column;
        gap: 10px;
      }

      .avatar {
        position: relative;
        width: 32px;
        height: 32px;
        border-radius: 9px;
        display: inline-grid;
        place-items: center;
        flex-shrink: 0;
        overflow: hidden;
        color: var(--accent-fg);
        background: var(--accent);
        font-weight: 800;
      }

      .avatar.round {
        border-radius: 50%;
      }

      .avatar img {
        position: absolute;
        inset: 0;
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .profilecard {
        padding: 22px;
        display: grid;
        grid-template-columns: min-content 1fr;
        gap: 18px;
        align-items: start;
      }

      .profilecard .avatar {
        width: 74px;
        height: 74px;
        border-radius: 18px;
        font-size: 26px;
      }

      .profilecard h1 {
        margin: 0 0 6px;
        font-family: var(--font-display);
        font-size: 28px;
        letter-spacing: -0.03em;
      }

      .profilecard p {
        margin: 0 0 12px;
        color: var(--text-secondary);
        line-height: 1.6;
      }

      .org-banner {
        border-bottom: 1px solid var(--border-default);
        background: var(--bg-secondary);
      }

      .org-banner .page {
        padding-top: 28px;
      }

      .sectitle {
        font-family: var(--font-display);
        font-weight: 700;
        letter-spacing: -0.01em;
      }

      .nowrap {
        white-space: nowrap;
      }

      .org-headside {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-shrink: 0;
      }

      .member-stack {
        display: flex;
        align-items: center;
        margin-right: 4px;
      }

      .member-stack .avatar {
        width: 30px;
        height: 30px;
        border: 2px solid var(--bg-secondary);
      }

      .org-layout {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 230px;
        gap: 24px;
        align-items: start;
      }

      .org-reporow {
        align-items: center;
        cursor: pointer;
      }

      .listrow .it {
        flex-shrink: 0;
        margin-top: 1px;
        color: var(--text-muted);
      }

      .listrow .ti {
        color: var(--text-primary);
        font-size: 14.5px;
        font-weight: 600;
      }

      .listrow:hover .ti {
        color: var(--accent);
      }

      .listrow .sub {
        margin-top: 3px;
        color: var(--text-muted);
        font-size: 12px;
      }

      .org-cardgrid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(310px, 1fr));
        gap: 14px;
      }

      .rcname {
        display: flex;
        align-items: center;
        gap: 8px;
        color: var(--accent);
        font-size: 15px;
        font-weight: 600;
      }

      .rcdesc {
        color: var(--text-secondary);
        font-size: 12.5px;
        line-height: 1.5;
      }

      .rcmeta {
        display: flex;
        align-items: center;
        gap: 16px;
        margin-top: 2px;
        color: var(--text-muted);
        font-size: 12px;
        flex-wrap: wrap;
      }

      .people-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
        gap: 12px;
      }

      .people-card {
        flex-direction: row;
        align-items: center;
        gap: 12px;
      }

      .people-card .avatar {
        width: 42px;
        height: 42px;
      }

      .org-reporow .repo-titleline {
        display: flex;
        align-items: center;
        gap: 8px;
        color: var(--text-primary);
        font-weight: 700;
      }

      .org-reporow .repo-subline {
        max-width: 540px;
        margin-top: 3px;
        overflow: hidden;
        color: var(--text-secondary);
        font-size: 12.5px;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .org-people-card {
        min-height: 220px;
        display: grid;
        place-items: center;
        padding: 32px;
        text-align: center;
      }

      .org-langbar {
        height: 8px;
        display: flex;
        overflow: hidden;
        border-radius: 4px;
        background: var(--bg-tertiary);
      }

      .org-langbar span {
        display: block;
      }

      .repohead {
        border-bottom: 1px solid var(--border-default);
        background: var(--bg-secondary);
      }

      .repohead-main {
        padding-top: 18px;
      }

      .repo-title {
        display: flex;
        align-items: center;
        gap: 7px;
        flex-wrap: wrap;
        font-size: 18px;
      }

      .repo-title .org {
        color: var(--accent);
        font-weight: 500;
        cursor: pointer;
      }

      .repo-title .org:hover {
        text-decoration: underline;
      }

      .repo-title .repo {
        color: var(--text-primary);
        font-weight: 700;
        cursor: pointer;
      }

      .repo-title .slash {
        color: var(--text-muted);
        font-weight: 500;
      }

      .repo-summary {
        margin-top: 8px;
        color: var(--text-secondary);
        max-width: 620px;
        font-size: 13.5px;
        line-height: 1.5;
      }

      .repohead-flex {
        display: flex;
        justify-content: space-between;
        gap: 18px;
        align-items: flex-start;
      }

      .repo-action-stack {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 10px;
      }

      .commit-strip {
        min-height: 48px;
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 0 14px;
        border-bottom: 1px solid var(--border-default);
        background: var(--bg-primary);
        font-size: 13px;
      }

      .sha {
        color: var(--accent);
        font-family: var(--font-mono);
        font-size: 12px;
      }

      .tabs {
        margin-top: 14px;
        gap: 2px;
      }

      .tab {
        position: relative;
        top: 1px;
        height: 38px;
        padding: 0 12px;
        display: inline-flex;
        align-items: center;
        gap: 7px;
        border-bottom: 2px solid transparent;
        border-radius: 6px 6px 0 0;
        color: var(--text-secondary);
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        transition: color 0.15s, background 0.15s;
      }

      .tab:hover {
        color: var(--text-primary);
        background: var(--hover);
      }

      .tab.on {
        color: var(--text-primary);
        border-bottom-color: var(--accent);
      }

      .tab .cnt {
        padding: 1px 6px;
        border-radius: 999px;
        color: var(--text-secondary);
        background: var(--bg-tertiary);
        font-size: 11px;
        font-weight: 600;
      }

      .tab.on .cnt {
        color: var(--accent);
        background: color-mix(in srgb, var(--accent) 16%, transparent);
      }

      .repo-layout {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 248px;
        gap: 24px;
        align-items: start;
      }

      .repo-layout > * {
        min-width: 0;
      }

      .filecard {
        overflow-x: auto;
      }

      .filebar,
      .card-h,
      .listhead,
      .digest-h {
        min-height: 46px;
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 0 14px;
        border-bottom: 1px solid var(--border-default);
        background: var(--bg-tertiary);
        font-size: 13px;
        font-weight: 600;
      }

      .card-h {
        min-height: 44px;
        padding: 0 16px;
      }

      .listhead {
        min-height: 48px;
        padding: 0 16px;
        font-weight: 400;
      }

      .breadcrumbs {
        display: flex;
        align-items: center;
        gap: 5px;
        min-width: 0;
        font-family: var(--font-mono);
        font-size: 14px;
      }

      .breadcrumbs button {
        color: var(--accent);
      }

      .breadcrumbs button:hover {
        text-decoration: underline;
      }

      table.ftable {
        width: 100%;
        border-collapse: collapse;
        font-size: 13.5px;
      }

      .ftable tr {
        border-bottom: 1px solid var(--border-subtle);
      }

      .ftable tr:last-child {
        border-bottom: 0;
      }

      .ftable tbody tr:hover {
        background: var(--row-hover);
      }

      .ftable td {
        padding: 8px 14px;
        vertical-align: middle;
      }

      .fname {
        display: inline-flex;
        align-items: center;
        gap: 9px;
        color: var(--text-primary);
        cursor: pointer;
      }

      .fname:hover {
        color: var(--accent);
        text-decoration: underline;
      }

      .ftable .age,
      .ftable .size {
        text-align: right;
        color: var(--text-muted);
        white-space: nowrap;
        font-size: 12.5px;
      }

      .sidecol {
        display: flex;
        flex-direction: column;
        gap: 14px;
        min-width: 0;
      }

      .card-b {
        padding: 15px;
      }

      .md {
        color: var(--text-primary);
        font-size: 14.5px;
        line-height: 1.7;
        overflow-wrap: anywhere;
      }

      .md h1,
      .md h2,
      .md h3 {
        font-family: var(--font-display);
        letter-spacing: -0.02em;
      }

      .md h1:first-child,
      .md h2:first-child,
      .md h3:first-child,
      .md p:first-child {
        margin-top: 0;
      }

      .md h1 {
        margin: 28px 0 14px;
        padding-bottom: 10px;
        border-bottom: 1px solid var(--border-default);
        font-size: 26px;
        font-weight: 700;
      }

      .md h2 {
        margin: 26px 0 12px;
        padding-bottom: 8px;
        border-bottom: 1px solid var(--border-subtle);
        font-size: 20px;
        font-weight: 700;
      }

      .md h3 {
        margin: 22px 0 10px;
        font-size: 16px;
        font-weight: 600;
      }

      .md p {
        margin: 0 0 14px;
      }

      .md a {
        color: var(--accent);
      }

      .md a:hover {
        text-decoration: underline;
      }

      .md code,
      .inline-code {
        padding: 0.12em 0.36em;
        border-radius: 4px;
        background: var(--code-bg);
        font-family: var(--font-mono);
        font-size: 12.5px;
      }

      .md pre,
      .codeblock {
        margin: 0 0 16px;
        padding: 14px 16px;
        overflow: auto;
        border: 1px solid var(--border-default);
        border-radius: 6px;
        background: var(--code-bg);
        font-family: var(--font-mono);
        font-size: 12.5px;
        line-height: 1.6;
        white-space: pre;
      }

      .md ul,
      .md ol {
        margin: 0 0 14px;
        padding-left: 24px;
      }

      .md li {
        margin: 4px 0;
      }

      .listrow {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 12px 16px;
        border-bottom: 1px solid var(--border-subtle);
      }

      .listrow:last-child {
        border-bottom: 0;
      }

      .listrow:hover {
        background: var(--row-hover);
      }

      .list-title {
        color: var(--text-primary);
        font-size: 14.5px;
        font-weight: 600;
        line-height: 1.4;
      }

      .list-sub {
        margin-top: 3px;
        color: var(--text-muted);
        font-size: 12px;
      }

      .label {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        border-radius: 999px;
        padding: 2px 8px;
        font-size: 11px;
        font-weight: 700;
      }

      .label-dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        flex-shrink: 0;
      }

      .timeline {
        position: relative;
      }

      .timeline.solo .tl-item::after {
        display: none;
      }

      .tl-item {
        position: relative;
        display: flex;
        align-items: flex-start;
        gap: 14px;
      }

      .tl-item + .tl-item {
        margin-top: 16px;
      }

      .tl-item::after {
        content: '';
        position: absolute;
        left: 15px;
        top: 34px;
        bottom: -16px;
        width: 2px;
        background: var(--border-default);
      }

      .tl-item:last-child::after {
        display: none;
      }

      .tl-dot {
        position: relative;
        z-index: 1;
        flex-shrink: 0;
        border-radius: 50%;
        box-shadow: 0 0 0 4px var(--bg-primary);
      }

      .tl-dot .avatar {
        width: 30px;
        height: 30px;
      }

      .thread-item {
        position: relative;
        flex: 1;
        min-width: 0;
        overflow: hidden;
        border: 1px solid var(--border-default);
        border-radius: 8px;
      }

      .thread-head {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 14px;
        border-bottom: 1px solid var(--border-default);
        background: var(--bg-tertiary);
        font-size: 13px;
        flex-wrap: wrap;
      }

      .thread-body {
        padding: 16px;
      }

      .digest {
        position: relative;
      }

      .digest::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 3px;
        background: linear-gradient(180deg, #8b5cf6, var(--accent));
      }

      .digest-h {
        align-items: flex-start;
        min-height: unset;
        padding: 14px 16px 12px 17px;
      }

      .digest-title {
        font-weight: 700;
        font-size: 15px;
        letter-spacing: -0.01em;
      }

      .digest-prose {
        margin: 0;
        color: var(--text-primary);
        font-size: 13.5px;
        line-height: 1.65;
      }

      .digest-stats {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-top: 14px;
      }

      .digest-stat {
        display: flex;
        align-items: baseline;
        gap: 6px;
        padding: 6px 11px;
        border: 1px solid var(--border-default);
        border-radius: 6px;
        background: var(--bg-tertiary);
        font-size: 12px;
      }

      .digest-stat .n {
        color: var(--text-primary);
        font-family: var(--font-mono);
        font-size: 14px;
        font-weight: 700;
      }

      .digest-highlights {
        display: flex;
        flex-direction: column;
        margin-top: 18px;
        overflow: hidden;
        border: 1px solid var(--border-default);
        border-radius: 8px;
      }

      .digest-hl {
        display: flex;
        align-items: center;
        gap: 14px;
        padding: 10px 16px;
        border-bottom: 1px solid var(--border-subtle);
        font-size: 13px;
      }

      .digest-hl:last-child {
        border-bottom: 0;
      }

      .digest-highlights .hl-repo {
        flex-shrink: 0;
        color: var(--accent);
        font-family: var(--font-mono);
        font-size: 12px;
      }

      .digest-highlights .hl-text {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        color: var(--text-secondary);
        font-size: 13.5px;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .digest-highlights .hl-age {
        flex-shrink: 0;
        color: var(--text-muted);
        font-size: 12.5px;
      }

      .digest-body {
        padding: 0 16px 14px;
      }

      .digest-foot {
        padding: 9px 16px;
        border-top: 1px solid var(--border-subtle);
        background: var(--bg-secondary);
        font-size: 11.5px;
      }

      .codecard {
        overflow: auto;
      }

      .codeview {
        width: 100%;
        border-collapse: collapse;
        font-family: var(--font-mono);
        font-size: 12.5px;
        line-height: 20px;
      }

      .codeview td {
        vertical-align: top;
      }

      .codeview .ln {
        width: 1%;
        padding: 0 13px 0 18px;
        color: var(--text-muted);
        text-align: right;
        user-select: none;
        border-right: 1px solid var(--border-subtle);
      }

      .codeview .code {
        min-width: 620px;
        padding: 0 16px;
        white-space: pre;
      }

      .codeview tr:hover td {
        background: var(--row-hover);
      }

      .empty,
      .loading,
      .error {
        padding: 48px 20px;
        text-align: center;
        color: var(--text-muted);
      }

      .error {
        color: var(--error);
      }

      .footer {
        margin-top: auto;
        padding: 32px 0;
        border-top: 1px solid var(--border-default);
        background: var(--bg-secondary);
      }

      .footercols {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 56px;
        flex-wrap: wrap;
      }

      .footer-top {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 24px;
        flex-wrap: wrap;
      }

      .footer-cols {
        display: flex;
        gap: 56px;
        flex-wrap: wrap;
      }

      .footer-bottom {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        flex-wrap: wrap;
        margin-top: 16px;
        color: var(--text-muted);
        font-size: 12px;
      }

      .divider {
        height: 1px;
        background: var(--border-default);
      }

      .footer h6 {
        margin: 0 0 12px;
        color: var(--text-muted);
        font-size: 11px;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        font-weight: 600;
      }

      .footer a,
      .footer button {
        display: block;
        padding: 4px 0;
        color: var(--text-secondary);
        font-size: 13px;
      }

      .footer a:hover,
      .footer button:hover {
        color: var(--text-primary);
      }

      .toast {
        position: fixed;
        right: 24px;
        bottom: 24px;
        z-index: 200;
        display: flex;
        align-items: center;
        gap: 9px;
        min-width: 230px;
        padding: 12px 16px;
        border-radius: 6px;
        color: var(--bg-primary);
        background: var(--text-primary);
        box-shadow: var(--shadow-lg);
        font-size: 13px;
        font-weight: 500;
      }

      .hide-desktop {
        display: none;
      }

      @media (max-width: 1100px) {
        .repo-layout {
          grid-template-columns: 1fr;
        }
      }

      @media (max-width: 820px) {
        :host {
          --nav-h: 104px;
        }

        .page {
          padding: 0 16px;
        }

        .topnav {
          height: auto;
        }

        .topnav .inner {
          min-height: var(--nav-h);
          flex-wrap: wrap;
          padding-top: 9px;
          padding-bottom: 9px;
        }

        .navsearch {
          order: 10;
          max-width: none;
          flex-basis: 100%;
        }

        .navlink.optional {
          display: none;
        }

        .hero {
          padding-top: 30px;
        }

        .hero h1 {
          font-size: 32px;
        }

        .repohead-flex,
        .profilecard,
        .org-layout {
          grid-template-columns: 1fr;
          flex-direction: column;
        }

        .repo-actions {
          width: 100%;
        }

        .repo-action-stack {
          width: 100%;
          align-items: flex-start;
        }

        .hide-mobile {
          display: none;
        }

        .hide-desktop {
          display: inline-flex;
        }
      }
    `,
  ];

  public async connectedCallback(): Promise<void> {
    await super.connectedCallback();
    this.route = this.parseHashRoute();
    this.searchQuery = this.route.query ?? '';
    window.addEventListener('hashchange', this.handleHashChange);
    window.addEventListener('keydown', this.handleGlobalKeydown);
    window.addEventListener('focus', this.handleWindowFocus);
    void this.refreshSession();
    void this.loadRouteData();
  }

  public async disconnectedCallback(): Promise<void> {
    window.removeEventListener('hashchange', this.handleHashChange);
    window.removeEventListener('keydown', this.handleGlobalKeydown);
    window.removeEventListener('focus', this.handleWindowFocus);
    this.authRefreshId += 1;
    this.clearAuthTimers();
    await super.disconnectedCallback();
  }

  public render(): TemplateResult {
    const currentRepo = this.route.view === 'repo'
      ? (this.route.tab === 'commits' ? this.commitsData?.repo ?? null : this.repoData?.repo ?? null)
      : this.route.view === 'file'
        ? this.fileData?.repo ?? null
        : null;
    const currentView = this.errorMessage
      ? html`<div class="page"><div class="error">${this.errorMessage}</div></div>`
      : this.renderCurrentView();
    // Views that render their own full-bleed banner/hero flush to the top nav own their
    // vertical spacing; wrapping them in .content would add a 24px gap above the banner.
    const viewOwnsSpacing = !this.errorMessage && (
      this.route.view === 'overview'
      || this.route.view === 'orgs'
      || this.route.view === 'org'
      || this.route.view === 'packages'
      || this.route.view === 'agent'
      || this.route.view === 'signin'
      || this.route.view === 'settings'
      || this.route.view === 'connections'
      || this.route.view === 'profile'
    );
    return html`
      <div class="app">
        ${this.renderTopNav()}
        <div class="appshell">
          <main class="appmain">
            ${currentRepo ? this.renderRepoHeader(currentRepo) : ''}
            ${viewOwnsSpacing ? currentView : html`<div class="content">${currentView}</div>`}
            ${this.renderFooter()}
          </main>
          ${this.activityPanelOpen ? this.renderActivityPanel() : ''}
        </div>
        ${this.toastMessage ? html`<div class="toast">${this.icon('CheckCircle')} ${this.toastMessage}</div>` : ''}
        ${this.proposeModalOpen
          ? html`
              <dotglobal-propose-modal
                .repo=${currentRepo}
                @dotglobal-close=${() => { this.proposeModalOpen = false; }}
                @dotglobal-navigate=${(eventArg: CustomEvent<{ hash: string }>) => this.navigate(eventArg.detail.hash)}
                @dotglobal-copy=${(eventArg: CustomEvent<{ text: string }>) => void this.copyText(eventArg.detail.text)}
              ></dotglobal-propose-modal>
            `
          : ''}
      </div>
    `;
  }

  private renderTopNav(): TemplateResult {
    return html`
      <nav class="topnav">
        <div class="page-wide inner">
          <button class="brand" @click=${() => this.navigate('#/')} aria-label="Open foss.global home">
            ${this.logo(26)}
            <span class="brandname">foss<span>.global</span></span>
          </button>
          <div class="navsearch">
            ${this.icon('Search')}
            <input
              .value=${this.searchQuery}
              placeholder="Search repositories..."
              @input=${(eventArg: Event) => {
                this.searchQuery = (eventArg.target as HTMLInputElement).value;
              }}
              @keydown=${(eventArg: KeyboardEvent) => this.handleSearchKeydown(eventArg)}
            />
            <span class="kbd">/</span>
          </div>
          <span class="grow"></span>
          <button class="navlink" @click=${() => this.navigate('#/')}>
            ${this.icon('Flame')} Explore
          </button>
          <button class=${`navlink ${this.route.view === 'orgs' ? 'on' : ''}`} @click=${() => this.navigate('#/orgs')}>
            ${this.icon('Building')} Organizations
          </button>
          <button class=${`navlink ${this.route.view === 'packages' ? 'on' : ''}`} @click=${() => this.navigate('#/packages')}>
            ${this.icon('Package')} Packages
          </button>
          <button class=${`navlink ${this.route.view === 'connections' ? 'on' : ''}`} @click=${() => this.navigate('#/connections')}>
            ${this.icon('Plug')} Connections
          </button>
          <button class=${`navlink ${this.activityPanelOpen ? 'on' : ''}`} @click=${() => this.toggleActivityPanel()}>
            <span class="activitydot"></span> Activity
          </button>
          <button class="navlink optional" @click=${() => this.navigate('#/agent')}>
            ${this.icon('Sparkles')} Propose
          </button>
          <button class="iconbtn" title="Toggle theme" @click=${() => this.toggleTheme()}>
            ${this.theme === 'dark' ? this.icon('Sun') : this.icon('Moon')}
          </button>
          ${this.authLoading
            ? html`<button class="btn sm" style="margin-left: 4px" disabled>Checking session…</button>`
            : this.auth
            ? html`
                <button class="iconbtn optional" title="Notifications">${this.icon('Bell')}</button>
                <div class="usermenu-wrap">
                  <button class="avatarbtn" aria-label="Account menu" @click=${() => { this.userMenuOpen = !this.userMenuOpen; }}>
                    ${this.renderAvatar(this.auth.user, '', true)}
                  </button>
                  ${this.userMenuOpen ? this.renderUserMenu(this.auth.user) : ''}
                </div>
              `
            : this.authAvailable
            ? html`<button class="btn sm" style="margin-left: 4px" @click=${() => this.navigate(this.signInHash())}>Sign in</button>`
            : html`<button class="btn sm" style="margin-left: 4px" disabled>Sign-in unavailable</button>`}
        </div>
      </nav>
    `;
  }

  private renderUserMenu(userArg: string): TemplateResult {
    return html`
      <div class="menu-backdrop" @click=${() => { this.userMenuOpen = false; }}></div>
      <div class="pop usermenu">
        <button class="um-head" @click=${() => this.navigateFromMenu(`#/user/${userArg}`)}>
          ${this.renderAvatar(userArg, '', true)}
          <span class="stack" style="min-width: 0; align-items: flex-start">
            <b style="font-size: 13px">${userArg}</b>
            <span class="muted mono" style="font-size: 11.5px">${userArg}</span>
          </span>
        </button>
        <div class="um-sep"></div>
        <button class="um-item" @click=${() => this.navigateFromMenu(`#/user/${userArg}`)}>${this.icon('User')} Your profile</button>
        <button class="um-item" @click=${() => this.navigateFromMenu('#/settings')}>${this.icon('Pencil')} Settings</button>
        <div class="um-sep"></div>
        <button class="um-item" @click=${() => { this.userMenuOpen = false; void this.signOut(); }}>${this.icon('LogOut')} Sign out</button>
      </div>
    `;
  }

  private navigateFromMenu(hashArg: string): void {
    this.userMenuOpen = false;
    this.navigate(hashArg);
  }

  private async refreshSession(): Promise<void> {
    if (this.authRetryTimer) {
      clearTimeout(this.authRetryTimer);
      this.authRetryTimer = undefined;
    }
    const refreshId = ++this.authRefreshId;
    let retryPending = false;
    try {
      const response = await fetch('/auth/session', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      const value = await response.json().catch(() => ({})) as {
        authenticated?: unknown;
        user?: { userId?: unknown; username?: unknown; displayName?: unknown };
        csrfToken?: unknown;
        absoluteExpiresAt?: unknown;
        error?: unknown;
      };
      if (refreshId !== this.authRefreshId) {
        return;
      }
      if (!response.ok) {
        if (
          response.status === 503
          && value.error === 'session_refresh_in_progress'
        ) {
          retryPending = true;
          this.authRetryTimer = setTimeout(() => {
            this.authRetryTimer = undefined;
            void this.refreshSession();
          }, 250);
          return;
        }
        this.authAvailable = false;
        this.clearAuthState();
        return;
      }
      this.authAvailable = true;
      if (value.authenticated === false) {
        this.clearAuthState();
        return;
      }
      if (
        value.authenticated !== true
        || typeof value.user?.userId !== 'string'
        || typeof value.user.username !== 'string'
        || typeof value.user.displayName !== 'string'
        || typeof value.csrfToken !== 'string'
        || typeof value.absoluteExpiresAt !== 'string'
        || !Number.isFinite(Date.parse(value.absoluteExpiresAt))
        || Date.parse(value.absoluteExpiresAt) <= Date.now()
      ) {
        this.authAvailable = false;
        this.clearAuthState();
        return;
      }
      this.auth = {
        userId: value.user.userId,
        user: value.user.username,
        displayName: value.user.displayName,
        csrfToken: value.csrfToken,
        absoluteExpiresAt: value.absoluteExpiresAt,
      };
      this.scheduleAuthExpiry(value.absoluteExpiresAt);
    } catch {
      if (refreshId === this.authRefreshId) {
        this.authAvailable = false;
        this.clearAuthState();
      }
    } finally {
      if (refreshId === this.authRefreshId && !retryPending) {
        this.authLoading = false;
      }
    }
  }

  private clearAuthState(): void {
    this.auth = null;
    this.userMenuOpen = false;
    if (this.authExpiryTimer) {
      clearTimeout(this.authExpiryTimer);
      this.authExpiryTimer = undefined;
    }
  }

  private clearAuthTimers(): void {
    this.clearAuthState();
    if (this.authRetryTimer) {
      clearTimeout(this.authRetryTimer);
      this.authRetryTimer = undefined;
    }
  }

  private scheduleAuthExpiry(absoluteExpiresAtArg: string): void {
    if (this.authExpiryTimer) {
      clearTimeout(this.authExpiryTimer);
    }
    const remainingMs = Date.parse(absoluteExpiresAtArg) - Date.now();
    this.authExpiryTimer = setTimeout(() => {
      this.authExpiryTimer = undefined;
      if (Date.parse(absoluteExpiresAtArg) <= Date.now()) {
        this.clearAuthState();
      }
      void this.refreshSession();
    }, Math.min(Math.max(1, remainingMs), 2_147_483_647));
  }

  /** The sign-in route, remembering the current page to return to afterwards. */
  private signInHash(): string {
    const currentHash = globalThis.location?.hash ?? '';
    return this.route.view !== 'signin' && currentHash.startsWith('#/')
      ? `#/signin?next=${encodeURIComponent(currentHash)}`
      : '#/signin';
  }

  private async signIn(): Promise<void> {
    if (!this.authAvailable) {
      this.showToast(describeAuthError('browser_auth_unavailable'));
      return;
    }
    const headers = new Headers({ Accept: 'application/json' });
    if (this.auth?.csrfToken) {
      headers.set('X-Foss-CSRF', this.auth.csrfToken);
    }
    const loginPath = this.route.view === 'signin' && this.route.returnTo
      ? `/auth/login?returnTo=${encodeURIComponent(`/${this.route.returnTo}`)}`
      : '/auth/login';
    try {
      const response = await fetch(loginPath, {
        method: 'POST',
        credentials: 'same-origin',
        headers,
      });
      const value = await response.json() as { authorizationUrl?: unknown; error?: unknown };
      if (!response.ok || typeof value.authorizationUrl !== 'string') {
        throw new Error(describeAuthError(
          typeof value.error === 'string' ? value.error : 'browser_auth_failed',
        ));
      }
      const authorizationUrl = new URL(value.authorizationUrl);
      if (authorizationUrl.protocol !== 'https:') {
        throw new Error('The identity provider returned an invalid authorization URL');
      }
      globalThis.location.assign(authorizationUrl.href);
    } catch (error) {
      this.showToast(error instanceof Error ? error.message : describeAuthError('browser_auth_failed'));
    }
  }

  private async signOut(): Promise<void> {
    const auth = this.auth;
    this.userMenuOpen = false;
    if (!auth) {
      this.navigate('#/');
      return;
    }
    try {
      const response = await fetch('/auth/logout', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Foss-CSRF': auth.csrfToken,
        },
      });
      const value = await response.json() as { logoutUrl?: unknown; error?: unknown };
      if (!response.ok || typeof value.logoutUrl !== 'string') {
        throw new Error(describeAuthError(
          typeof value.error === 'string' ? value.error : 'browser_auth_failed',
        ));
      }
      // The session is already revoked; the identity provider ends its own
      // session and returns the browser to /auth/logged-out.
      const logoutUrl = new URL(value.logoutUrl);
      if (logoutUrl.protocol !== 'https:' || !logoutUrl.searchParams.get('state')) {
        throw new Error('The server returned an invalid identity-provider logout URL');
      }
      this.auth = null;
      globalThis.location.assign(logoutUrl.href);
    } catch (error) {
      await this.refreshSession();
      this.showToast(error instanceof Error ? error.message : describeAuthError('browser_auth_failed'));
    }
  }

  private renderRepoStat(
    iconArg: string,
    labelArg: string,
    valueArg: number,
    disabledArg = false,
    activeArg = false,
    clickHandlerArg?: () => void,
  ): TemplateResult {
    return html`
      <button
        class=${`statbtn ${activeArg ? 'on' : ''}`}
        title=${disabledArg ? 'Not available on foss.global' : `${labelArg} locally in this browser`}
        ?disabled=${disabledArg}
        @click=${() => {
          if (!disabledArg && clickHandlerArg) {
            clickHandlerArg();
          }
        }}
      >
        <span class="l">${this.icon(iconArg, 14)} ${labelArg}</span>
        <span class="n">${valueArg}</span>
      </button>
    `;
  }

  private renderCurrentView(): TemplateResult {
    if (this.loading && !this.hasDataForRoute()) {
      return html`<div class="page"><div class="loading">Loading public code surface...</div></div>`;
    }
    if (!this.hasDataForRoute() && this.errorMessage) {
      return html`<div class="page"><div class="empty">${this.errorMessage}</div></div>`;
    }
    if (!this.hasDataForRoute()) {
      return html`<div class="page"><div class="empty">No current data for this route.</div></div>`;
    }

    if (this.route.view === 'orgs') {
      return this.renderAllOrgs();
    }
    if (this.route.view === 'org') {
      return this.renderOrgView();
    }
    if (this.route.view === 'packages') {
      return this.renderPackagesView();
    }
    if (this.route.view === 'repo') {
      return this.renderRepoView();
    }
    if (this.route.view === 'file') {
      return this.renderFileView();
    }
    if (this.route.view === 'agent') {
      return this.renderAgentView();
    }
    if (this.route.view === 'signin') {
      return this.renderSignInView();
    }
    if (this.route.view === 'settings') {
      return this.renderSettingsView();
    }
    if (this.route.view === 'connections') {
      return html`<dotglobal-forges></dotglobal-forges>`;
    }
    if (this.route.view === 'profile') {
      return this.renderProfileView();
    }
    return this.renderOverview();
  }

  private renderSignInView(): TemplateResult {
    return html`
      <dotglobal-sign-in
        .errorCode=${this.route.authError ?? ''}
        .available=${this.authAvailable}
        @dotglobal-signin=${() => void this.signIn()}
        @dotglobal-navigate=${(eventArg: CustomEvent<{ hash: string }>) => this.navigate(eventArg.detail.hash)}
      ></dotglobal-sign-in>
    `;
  }

  private renderPackagesView(): TemplateResult {
    return html`
      <dotglobal-packages
        .kind=${this.route.packageKind ?? ''}
        .owner=${this.route.packageOwner ?? ''}
        .name=${this.route.packageName ?? ''}
        .csrfToken=${this.auth?.csrfToken ?? ''}
        @dotglobal-auth-refresh=${() => void this.refreshSession()}
        @dotglobal-navigate=${(eventArg: CustomEvent<{ hash: string }>) => this.navigate(eventArg.detail.hash)}
        @dotglobal-copy=${(eventArg: CustomEvent<{ text: string }>) => void this.copyText(eventArg.detail.text)}
      ></dotglobal-packages>
    `;
  }

  private renderSettingsView(): TemplateResult {
    if (!this.auth) {
      return this.renderSignInView();
    }
    return html`
      <dotglobal-settings
        .user=${this.auth.user}
        .displayName=${this.auth.displayName}
        .absoluteExpiresAt=${this.auth.absoluteExpiresAt}
        @dotglobal-navigate=${(eventArg: CustomEvent<{ hash: string }>) => this.navigate(eventArg.detail.hash)}
        @dotglobal-signout=${() => void this.signOut()}
        @dotglobal-toast=${(eventArg: CustomEvent<{ message: string }>) => this.showToast(eventArg.detail.message)}
      ></dotglobal-settings>
    `;
  }

  private renderProfileView(): TemplateResult {
    const profileUser = this.route.profileUser ?? this.auth?.user ?? '';
    const isOwn = Boolean(this.auth && this.auth.user === profileUser);
    return html`
      <dotglobal-user-profile
        .login=${profileUser}
        .isOwnProfile=${isOwn}
        @dotglobal-navigate=${(eventArg: CustomEvent<{ hash: string }>) => this.navigate(eventArg.detail.hash)}
        @dotglobal-toast=${(eventArg: CustomEvent<{ message: string }>) => this.showToast(eventArg.detail.message)}
      ></dotglobal-user-profile>
    `;
  }

  private renderOverview(): TemplateResult {
    return html`
      <dotglobal-explore
        .data=${this.overviewData}
        .query=${this.route.query ?? ''}
        @dotglobal-navigate=${(eventArg: CustomEvent<{ hash: string }>) => this.navigate(eventArg.detail.hash)}
        @dotglobal-toast=${(eventArg: CustomEvent<{ message: string }>) => this.showToast(eventArg.detail.message)}
      ></dotglobal-explore>
    `;
  }

  private renderAllOrgs(): TemplateResult {
    return html`
      <dotglobal-all-orgs
        .data=${this.overviewData}
        @dotglobal-navigate=${(eventArg: CustomEvent<{ hash: string }>) => this.navigate(eventArg.detail.hash)}
      ></dotglobal-all-orgs>
    `;
  }

  private renderOrgView(): TemplateResult {
    return html`
      <dotglobal-org-view
        .data=${this.orgData}
        @dotglobal-navigate=${(eventArg: CustomEvent<{ hash: string }>) => this.navigate(eventArg.detail.hash)}
        @dotglobal-toast=${(eventArg: CustomEvent<{ message: string }>) => this.showToast(eventArg.detail.message)}
      ></dotglobal-org-view>
    `;
  }

  private renderRepoHeader(repoArg: interfaces.IPublicRepo): TemplateResult {
    const activeTab = this.route.tab ?? 'code';
    const repoInteraction = this.repoInteractions[repoArg.slug] ?? {};
    const isWatching = Boolean(repoInteraction.watching);
    const isStarred = Boolean(repoInteraction.starred);
    return html`
      <header class="repohead">
        <div class="page repohead-main">
          <div class="repohead-flex">
            <div>
              <div class="repo-title">
                ${this.renderAvatar(repoArg.org, this.getOrgAvatarUrl(repoArg.org))}
                <button class="org" @click=${() => this.navigate(this.getOrgHash(repoArg.org))}>${repoArg.org}</button>
                <span class="slash">/</span>
                <button class="repo" @click=${() => this.navigate(this.getRepoHash(repoArg.org, repoArg.name))}>${repoArg.name}</button>
                <span class="badge outline sq">${this.icon('Globe')} Public</span>
                ${repoArg.archived ? html`<span class="badge outline sq">archived</span>` : ''}
              </div>
              <div class="repo-summary">${repoArg.description || 'No repository description provided.'}</div>
              ${repoArg.topics.length
                ? html`<div class="chips" style="margin-top: 10px">${repoArg.topics.slice(0, 8).map((topic) => html`<span class="badge accent">${topic}</span>`)}</div>`
                : ''}
              <div class="repo-meta">
                ${repoArg.language ? html`<span class="meta-item"><span class="lang-dot" style=${`background:${languageColor(repoArg.language)}`}></span>${repoArg.language}</span>` : ''}
                <span class="meta-item">${this.icon('Eye')} ${repoArg.watchersCount}</span>
                <span class="meta-item">${this.icon('GitFork')} ${repoArg.forksCount}</span>
                <span class="meta-item">Updated ${this.timeAgo(repoArg.updatedAt)}</span>
              </div>
            </div>
            <div class="repo-action-stack">
              <div class="repo-actions">
                ${this.renderRepoStat('Eye', isWatching ? 'Watching' : 'Watch', repoArg.watchersCount + (isWatching ? 1 : 0), false, isWatching, () => this.toggleRepoInteraction(repoArg, 'watching'))}
                ${this.renderRepoStat('Star', isStarred ? 'Starred' : 'Star', repoArg.starsCount + (isStarred ? 1 : 0), false, isStarred, () => this.toggleRepoInteraction(repoArg, 'starred'))}
                ${this.renderRepoStat('GitFork', 'Fork', repoArg.forksCount, true)}
              </div>
              <div class="repo-actions" style="position: relative">
                <button class="btn sm primary" @click=${() => { this.proposeModalOpen = true; }}>${this.icon('Sparkles')} Propose a change</button>
              </div>
            </div>
          </div>
          <div class="tabs">
            <button class=${`tab ${activeTab === 'code' ? 'on' : ''}`} @click=${() => this.navigate(this.getRepoHash(repoArg.org, repoArg.name))}>
              ${this.icon('Code')} Code
            </button>
            <button class=${`tab ${activeTab === 'commits' ? 'on' : ''}`} @click=${() => this.navigate(this.getRepoHash(repoArg.org, repoArg.name, 'commits'))}>
              ${this.icon('GitCommitHorizontal')} Commits
            </button>
            <button class=${`tab ${activeTab === 'issues' ? 'on' : ''}`} @click=${() => this.navigate(this.getRepoHash(repoArg.org, repoArg.name, 'issues'))}>
              ${this.icon('CircleDot')} Issues <span class="cnt">${repoArg.openIssuesCount}</span>
            </button>
            <button class=${`tab ${activeTab === 'pulls' ? 'on' : ''}`} @click=${() => this.navigate(this.getRepoHash(repoArg.org, repoArg.name, 'pulls'))}>
              ${this.icon('GitPullRequest')} Pull requests <span class="cnt">${repoArg.openPrCount}</span>
            </button>
            <button class=${`tab ${activeTab === 'discussions' ? 'on' : ''}`} @click=${() => this.navigate(this.getRepoHash(repoArg.org, repoArg.name, 'discussions'))}>
              ${this.icon('MessageSquare')} Discussions
            </button>
            <button class=${`tab ${activeTab === 'releases' ? 'on' : ''}`} @click=${() => this.navigate(this.getRepoHash(repoArg.org, repoArg.name, 'releases'))}>
              ${this.icon('Tag')} Releases <span class="cnt">${repoArg.releaseCount}</span>
            </button>
          </div>
        </div>
      </header>
    `;
  }

  private renderRepoView(): TemplateResult {
    if (this.route.tab === 'commits') {
      return this.renderCommitsTab();
    }

    const data = this.repoData;
    if (!data?.repo) {
      return html`<div class="page"><div class="empty">Repository not found.</div></div>`;
    }

    if (this.route.tab === 'issues') {
      return this.renderIssuesTab(data);
    }

    if (this.route.tab === 'pulls') {
      return this.renderPullsTab(data);
    }

    if (this.route.tab === 'discussions') {
      return this.renderDiscussionsTab(data);
    }

    if (this.route.tab === 'releases') {
      return this.renderReleasesTab(data);
    }

    return html`
      <dotglobal-repo-home
        .repo=${data.repo}
        .contents=${data.contents}
        .path=${this.route.path ?? ''}
        .readme=${data.readme}
        .releases=${data.releases}
        .activity=${data.activity}
        .commits=${data.commits}
        .languages=${data.languages}
        @dotglobal-navigate=${(eventArg: CustomEvent<{ hash: string }>) => this.navigate(eventArg.detail.hash)}
      ></dotglobal-repo-home>
    `;
  }

  private renderFileView(): TemplateResult {
    const data = this.fileData;
    const repo = data?.repo;
    const file = data?.file;
    if (!repo || !file) {
      return html`<div class="page"><div class="empty">File not found.</div></div>`;
    }

    return html`
      <dotglobal-file-view
        .repo=${repo}
        .file=${file}
        .commits=${data.commits}
        @dotglobal-navigate=${(eventArg: CustomEvent<{ hash: string }>) => this.navigate(eventArg.detail.hash)}
        @dotglobal-copy=${(eventArg: CustomEvent<{ text: string }>) => void this.copyText(eventArg.detail.text)}
      ></dotglobal-file-view>
    `;
  }

  private renderIssuesTab(dataArg: interfaces.IReq_PublicRepo['response']): TemplateResult {
    return html`
      <dotglobal-issues
        .data=${dataArg}
        .selectedIssueNumber=${Number(this.route.path ?? 0)}
        @dotglobal-navigate=${(eventArg: CustomEvent<{ hash: string }>) => this.navigate(eventArg.detail.hash)}
        @dotglobal-propose=${() => { this.proposeModalOpen = true; }}
      ></dotglobal-issues>
    `;
  }

  private renderPullsTab(dataArg: interfaces.IReq_PublicRepo['response']): TemplateResult {
    return html`
      <dotglobal-pulls
        .data=${dataArg}
        .selectedPullRequestNumber=${Number(this.route.path ?? 0)}
        @dotglobal-navigate=${(eventArg: CustomEvent<{ hash: string }>) => this.navigate(eventArg.detail.hash)}
      ></dotglobal-pulls>
    `;
  }

  private renderReleasesTab(dataArg: interfaces.IReq_PublicRepo['response']): TemplateResult {
    return html`
      <dotglobal-releases
        .repo=${dataArg.repo}
        .releases=${dataArg.releases}
        @dotglobal-navigate=${(eventArg: CustomEvent<{ hash: string }>) => this.navigate(eventArg.detail.hash)}
        @dotglobal-toast=${(eventArg: CustomEvent<{ message: string }>) => this.showToast(eventArg.detail.message)}
        @dotglobal-copy=${(eventArg: CustomEvent<{ text: string }>) => void this.copyText(eventArg.detail.text)}
      ></dotglobal-releases>
    `;
  }

  private renderDiscussionsTab(dataArg: interfaces.IReq_PublicRepo['response']): TemplateResult {
    const repo = dataArg.repo!;
    return html`
      <div class="page">
        <div class="row" style="align-items: flex-start; gap: 24px">
          <div class="grow listcard" style="min-width: 0">
            <div class="listhead">
              ${this.icon('MessageSquare')}
              <b style="font-size: 13px">Discussions unavailable</b>
              <span style="flex: 1"></span>
            </div>
            <div class="empty">
              The public discussion API is not available here yet.
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderCommitsTab(): TemplateResult {
    const data = this.commitsData;
    if (!data?.repo) {
      return html`<div class="page"><div class="empty">Repository not found.</div></div>`;
    }
    if (this.route.path) {
      return html`
        <dotglobal-commit-detail
          .data=${data}
          @dotglobal-navigate=${(eventArg: CustomEvent<{ hash: string }>) => this.navigate(eventArg.detail.hash)}
          @dotglobal-toast=${(eventArg: CustomEvent<{ message: string }>) => this.showToast(eventArg.detail.message)}
        ></dotglobal-commit-detail>
      `;
    }
    return html`
      <dotglobal-commits
        .data=${data}
        @dotglobal-navigate=${(eventArg: CustomEvent<{ hash: string }>) => this.navigate(eventArg.detail.hash)}
        @dotglobal-toast=${(eventArg: CustomEvent<{ message: string }>) => this.showToast(eventArg.detail.message)}
      ></dotglobal-commits>
    `;
  }

  private renderAgentView(): TemplateResult {
    return html`
      <dotglobal-agent-view
        @dotglobal-navigate=${(eventArg: CustomEvent<{ hash: string }>) => this.navigate(eventArg.detail.hash)}
        @dotglobal-copy=${(eventArg: CustomEvent<{ text: string }>) => void this.copyText(eventArg.detail.text)}
      ></dotglobal-agent-view>
    `;
  }

  private renderActivityDigest(activityArg: interfaces.IPublicActivityEntry[], titleArg = "What's been shipped"): TemplateResult {
    return html`
      <dotglobal-activity-digest
        .activity=${activityArg}
        .heading=${titleArg}
        @dotglobal-toast=${(eventArg: CustomEvent<{ message: string }>) => this.showToast(eventArg.detail.message)}
      ></dotglobal-activity-digest>
    `;
  }

  private renderActivityPanel(): TemplateResult {
    return html`
      <dotglobal-activity-panel
        .commits=${this.getCurrentCommitFeed()}
        .orgAvatarUrls=${this.getOrgAvatarMap()}
        @dotglobal-close=${() => { this.activityPanelOpen = false; globalThis.localStorage?.setItem('fg.activityPanel', '0'); }}
        @dotglobal-navigate=${(eventArg: CustomEvent<{ hash: string }>) => this.navigate(eventArg.detail.hash)}
      ></dotglobal-activity-panel>
    `;
  }

  private renderMarkdown(markdownArg: string, maxLinesArg = 120): TemplateResult[] {
    return renderMarkdownShared(markdownArg, maxLinesArg);
  }

  private renderAvatar(labelArg: string, avatarUrlArg: string, roundArg = false): TemplateResult {
    return html`
      <span class=${`avatar ${roundArg ? 'round' : ''}`}>
        ${this.getInitials(labelArg)}
        ${avatarUrlArg
          ? html`<img src=${avatarUrlArg} alt="" loading="lazy" referrerpolicy="no-referrer" @error=${(eventArg: Event) => { (eventArg.currentTarget as HTMLImageElement).style.display = 'none'; }} />`
          : ''}
      </span>
    `;
  }

  private renderFooter(): TemplateResult {
    return html`
      <footer class="footer">
        <div class="page">
          <div class="footer-top">
            <div style="max-width: 280px">
              <div class="brand" style="cursor: default">
                ${this.logo(24)}
                <div class="brandname" style="font-size: 15px">foss<span>.global</span></div>
              </div>
              <div class="muted" style="font-size: 12.5px; margin-top: 12px; line-height: 1.6">
                A fast, public front end for the open-source world. Browse, discuss, and propose changes - no fork required.
              </div>
            </div>
            <div class="footer-cols">
              <div>
                <h6>Platform</h6>
                <button @click=${() => this.navigate('#/')}>Explore</button>
                <button @click=${() => this.toggleActivityPanel()}>Activity</button>
                <button @click=${() => this.navigate('#/agent')}>Propose agent</button>
                <a href="https://status.foss.global" target="_blank" rel="noopener">Status</a>
              </div>
              <div>
                <h6>Open source</h6>
                <button @click=${() => this.navigate('#/agent')}>How it works</button>
                <button @click=${() => this.navigate('#/')}>Self-host</button>
                <button @click=${() => this.navigate(this.getOrgHash('foss.global'))}>Mirror your repo</button>
                <button @click=${() => this.navigate(this.getOrgHash('foss.global'))}>Source code</button>
              </div>
              <div>
                <h6>Community</h6>
                <a href="https://community.foss.global" target="_blank" rel="noopener">Discussions</a>
                <a href="https://community.foss.global" target="_blank" rel="noopener">Guidelines</a>
                <button @click=${() => this.navigate(this.getOrgHash('serve.zone'))}>Contributors</button>
                <button @click=${() => this.navigate(this.getOrgHash('push.rocks'))}>Sponsors</button>
              </div>
              <div>
                <h6>Legal</h6>
                <a href="https://legal.task.vc" target="_blank" rel="noopener">Privacy</a>
                <a href="https://legal.task.vc" target="_blank" rel="noopener">Terms</a>
                <a href="https://legal.task.vc" target="_blank" rel="noopener">Licenses</a>
              </div>
            </div>
          </div>
          <div class="divider" style="margin: 24px 0 0"></div>
          <div class="footer-bottom">
            <span>© 2026 foss.global - content under each project's own license.</span>
          </div>
        </div>
      </footer>
    `;
  }

  private async loadRouteData(): Promise<void> {
    const loadId = ++this.activeLoadId;
    const routeAtLoad = this.route;
    this.loading = true;
    this.errorMessage = '';

    try {
      if (routeAtLoad.view === 'overview' || routeAtLoad.view === 'orgs') {
        const typedRequest = new domtools.plugins.typedrequest.TypedRequest<interfaces.IReq_PublicOverview>('/typedrequest', 'publicOverview');
        const overview = await typedRequest.fire({ query: routeAtLoad.query });
        if (loadId === this.activeLoadId) {
          this.overviewData = overview;
          this.overviewDataKey = this.getOverviewDataKey(routeAtLoad);
        }
      } else if (routeAtLoad.view === 'org' && routeAtLoad.org) {
        const typedRequest = new domtools.plugins.typedrequest.TypedRequest<interfaces.IReq_PublicOrg>('/typedrequest', 'publicOrg');
        const orgData = await typedRequest.fire({ org: routeAtLoad.org });
        if (loadId === this.activeLoadId) {
          this.orgData = orgData;
          this.orgDataKey = this.getOrgDataKey(routeAtLoad);
        }
      } else if (routeAtLoad.view === 'repo' && routeAtLoad.tab === 'commits' && routeAtLoad.org && routeAtLoad.repo) {
        const typedRequest = new domtools.plugins.typedrequest.TypedRequest<interfaces.IReq_PublicCommits>('/typedrequest', 'publicCommits');
        const commitsData = await typedRequest.fire({ org: routeAtLoad.org, repo: routeAtLoad.repo, sha: routeAtLoad.path || undefined });
        if (loadId === this.activeLoadId) {
          this.commitsData = commitsData;
          this.commitsDataKey = this.getCommitsDataKey(routeAtLoad);
        }
      } else if (routeAtLoad.view === 'repo' && routeAtLoad.org && routeAtLoad.repo) {
        const typedRequest = new domtools.plugins.typedrequest.TypedRequest<interfaces.IReq_PublicRepo>('/typedrequest', 'publicRepo');
        const repoData = await typedRequest.fire({
          org: routeAtLoad.org,
          repo: routeAtLoad.repo,
          path: this.getRepoContentPathForRoute(routeAtLoad),
          issueNumber: this.getIssueNumberForRoute(routeAtLoad),
          pullRequestNumber: this.getPullRequestNumberForRoute(routeAtLoad),
        });
        if (loadId === this.activeLoadId) {
          this.repoData = repoData;
          this.repoDataKey = this.getRepoDataKey(routeAtLoad);
        }
      } else if (routeAtLoad.view === 'file' && routeAtLoad.org && routeAtLoad.repo && routeAtLoad.path) {
        const typedRequest = new domtools.plugins.typedrequest.TypedRequest<interfaces.IReq_PublicFile>('/typedrequest', 'publicFile');
        const fileData = await typedRequest.fire({ org: routeAtLoad.org, repo: routeAtLoad.repo, path: routeAtLoad.path });
        if (loadId === this.activeLoadId) {
          this.fileData = fileData;
          this.fileDataKey = this.getFileDataKey(routeAtLoad);
        }
      }
    } catch (error) {
      if (loadId === this.activeLoadId) {
        this.errorMessage = error instanceof Error ? error.message : 'Unable to load public code data.';
      }
    } finally {
      if (loadId === this.activeLoadId) {
        this.loading = false;
      }
    }
  }

  private parseHashRoute(): IRouteState {
    const rawHash = globalThis.location?.hash?.slice(1) || '/';
    const [pathPart, queryPart = ''] = rawHash.split('?');
    const queryParams = new URLSearchParams(queryPart);
    const parts = pathPart.split('/').filter(Boolean).map((part) => decodeURIComponent(part));

    if (parts[0] === 'agent') {
      return { view: 'agent' };
    }

    if (parts[0] === 'orgs') {
      return { view: 'orgs' };
    }

    if (parts[0] === 'packages') {
      const packageKind = parts[1] === 'npm' || parts[1] === 'generic' || parts[1] === 'oci'
        ? parts[1]
        : undefined;
      return {
        view: 'packages',
        packageKind,
        packageOwner: packageKind ? parts[2] : undefined,
        packageName: packageKind ? parts[3] : undefined,
      };
    }

    if (parts[0] === 'signin') {
      const authError = queryParams.get('authError');
      const next = queryParams.get('next');
      return {
        view: 'signin',
        authError: authError && authError.length <= 64 ? authError : undefined,
        returnTo: next && next.startsWith('#/') && !next.startsWith('#/signin') && next.length <= 200
          ? next
          : undefined,
      };
    }

    if (parts[0] === 'settings') {
      return { view: 'settings' };
    }
    if (parts[0] === 'connections') {
      return { view: 'connections' };
    }

    if (parts[0] === 'user' && parts[1]) {
      return { view: 'profile', profileUser: parts[1] };
    }

    if (parts[0] === 'org' && parts[1]) {
      return { view: 'org', org: parts[1] };
    }

    if (parts[0] === 'repo' && parts[1] && parts[2]) {
      if (parts[3] === 'commits') {
        return { view: 'repo', org: parts[1], repo: parts[2], tab: 'commits', path: parts[4] };
      }
      if (parts[3] === 'issues') {
        return { view: 'repo', org: parts[1], repo: parts[2], tab: 'issues', path: parts[4] };
      }
      if (parts[3] === 'pulls') {
        return { view: 'repo', org: parts[1], repo: parts[2], tab: 'pulls', path: parts[4] };
      }
      if (parts[3] === 'releases') {
        return { view: 'repo', org: parts[1], repo: parts[2], tab: 'releases' };
      }
      if (parts[3] === 'discussions') {
        return { view: 'repo', org: parts[1], repo: parts[2], tab: 'discussions' };
      }
      if (parts[3] === 'src') {
        return { view: 'repo', org: parts[1], repo: parts[2], tab: 'code', path: parts.slice(4).join('/') };
      }
      return { view: 'repo', org: parts[1], repo: parts[2], tab: 'code' };
    }

    if (parts[0] === 'file' && parts[1] && parts[2]) {
      return { view: 'file', org: parts[1], repo: parts[2], path: parts.slice(3).join('/') };
    }

    return { view: 'overview', query: queryParams.get('q') || undefined };
  }

  private hasDataForRoute(): boolean {
    if (this.route.view === 'overview' || this.route.view === 'orgs') {
      return Boolean(this.overviewData) && this.overviewDataKey === this.getOverviewDataKey();
    }
    if (this.route.view === 'org') {
      return Boolean(this.orgData) && this.orgDataKey === this.getOrgDataKey();
    }
    if (this.route.view === 'repo') {
      if (this.route.tab === 'commits') {
        return Boolean(this.commitsData) && this.commitsDataKey === this.getCommitsDataKey();
      }
      return Boolean(this.repoData) && this.repoDataKey === this.getRepoDataKey();
    }
    if (this.route.view === 'file') {
      return Boolean(this.fileData) && this.fileDataKey === this.getFileDataKey();
    }
    return true;
  }

  private getOverviewDataKey(routeArg: IRouteState = this.route): string {
    return routeArg.query ?? '';
  }

  private getOrgDataKey(routeArg: IRouteState = this.route): string {
    return routeArg.org ?? '';
  }

  private getRepoDataKey(routeArg: IRouteState = this.route): string {
    const tab = routeArg.tab ?? 'code';
    const routePath = tab === 'code' || tab === 'issues' || tab === 'pulls' ? routeArg.path ?? '' : '';
    return `${routeArg.org ?? ''}/${routeArg.repo ?? ''}/${tab}/${routePath}`;
  }

  private getCommitsDataKey(routeArg: IRouteState = this.route): string {
    return `${routeArg.org ?? ''}/${routeArg.repo ?? ''}/${routeArg.path ?? ''}`;
  }

  private getFileDataKey(routeArg: IRouteState = this.route): string {
    return `${routeArg.org ?? ''}/${routeArg.repo ?? ''}/${routeArg.path ?? ''}`;
  }

  private getRepoContentPathForRoute(routeArg: IRouteState): string | undefined {
    return (routeArg.tab ?? 'code') === 'code' ? routeArg.path : undefined;
  }

  private getIssueNumberForRoute(routeArg: IRouteState): number | undefined {
    if (routeArg.tab !== 'issues' || !routeArg.path) {
      return undefined;
    }
    const issueNumber = Number(routeArg.path);
    return Number.isFinite(issueNumber) && issueNumber > 0 ? issueNumber : undefined;
  }

  private getPullRequestNumberForRoute(routeArg: IRouteState): number | undefined {
    if (routeArg.tab !== 'pulls' || !routeArg.path) {
      return undefined;
    }
    const pullRequestNumber = Number(routeArg.path);
    return Number.isFinite(pullRequestNumber) && pullRequestNumber > 0
      ? pullRequestNumber
      : undefined;
  }

  private handleGlobalKeydown = (eventArg: KeyboardEvent) => {
    const eventPath = eventArg.composedPath();
    const isFromEditable = eventPath.some((item) => {
      return item instanceof HTMLInputElement || item instanceof HTMLTextAreaElement || (item instanceof HTMLElement && item.isContentEditable);
    });
    if (eventArg.key === '/' && !isFromEditable) {
      eventArg.preventDefault();
      const input = this.shadowRoot?.querySelector('.navsearch input') as HTMLInputElement | null;
      input?.focus();
    }
  };

  private handleSearchKeydown(eventArg: KeyboardEvent): void {
    if (eventArg.key !== 'Enter') {
      return;
    }
    const query = this.searchQuery.trim();
    this.navigate(query ? `#/?q=${encodeURIComponent(query)}` : '#/');
  }

  private navigate(hashArg: string): void {
    if (globalThis.location.hash === hashArg) {
      this.handleHashChange();
      return;
    }
    globalThis.location.hash = hashArg;
    globalThis.scrollTo?.(0, 0);
  }

  private getCurrentCommitFeed(): interfaces.IPublicCommit[] {
    if (this.route.view === 'repo') {
      if (this.route.tab === 'commits') {
        return this.commitsData?.commits ?? this.repoData?.commits ?? [];
      }
      return this.repoData?.commits ?? [];
    }
    if (this.route.view === 'file') {
      return this.fileData?.commits ?? [];
    }
    if (this.route.view === 'org') {
      return this.orgData?.commitFeed ?? [];
    }
    return this.overviewData?.commitFeed ?? [];
  }

  private getOrgHash(orgArg: string): string {
    return `#/org/${encodeURIComponent(orgArg)}`;
  }

  private getRepoHash(orgArg: string, repoArg: string, tabArg: TRepoTab = 'code', pathArg = ''): string {
    const base = `#/repo/${encodeURIComponent(orgArg)}/${encodeURIComponent(repoArg)}`;
    if (tabArg === 'commits') {
      return pathArg ? `${base}/commits/${this.encodePath(pathArg)}` : `${base}/commits`;
    }
    if (tabArg === 'issues' || tabArg === 'pulls') {
      return pathArg ? `${base}/${tabArg}/${this.encodePath(pathArg)}` : `${base}/${tabArg}`;
    }
    if (tabArg === 'releases' || tabArg === 'discussions') {
      return `${base}/${tabArg}`;
    }
    return pathArg ? `${base}/src/${this.encodePath(pathArg)}` : base;
  }


  private getFileHash(orgArg: string, repoArg: string, pathArg: string): string {
    return `#/file/${encodeURIComponent(orgArg)}/${encodeURIComponent(repoArg)}/${this.encodePath(pathArg)}`;
  }

  private encodePath(pathArg: string): string {
    return pathArg.split('/').filter(Boolean).map((part) => encodeURIComponent(part)).join('/');
  }

  private toggleTheme(): void {
    this.theme = this.theme === 'dark' ? 'light' : 'dark';
    globalThis.localStorage?.setItem('fg.theme', this.theme);
  }

  private toggleActivityPanel(): void {
    this.activityPanelOpen = !this.activityPanelOpen;
    globalThis.localStorage?.setItem('fg.activityPanel', this.activityPanelOpen ? '1' : '0');
  }

  private toggleRepoInteraction(repoArg: interfaces.IPublicRepo, interactionArg: keyof IRepoInteractionState): void {
    const currentState = this.repoInteractions[repoArg.slug] ?? {};
    const nextValue = !currentState[interactionArg];
    this.repoInteractions = {
      ...this.repoInteractions,
      [repoArg.slug]: {
        ...currentState,
        [interactionArg]: nextValue,
      },
    };
    const label = interactionArg === 'starred' ? 'starred' : 'watching';
    this.showToast(nextValue ? `Locally ${label} ${repoArg.slug}` : `Removed local ${label} for ${repoArg.slug}`);
  }

  private showToast(messageArg: string): void {
    this.toastMessage = messageArg;
    setTimeout(() => {
      if (this.toastMessage === messageArg) {
        this.toastMessage = '';
      }
    }, 2200);
  }

  private async copyText(textArg: string): Promise<void> {
    await navigator.clipboard.writeText(textArg);
    this.showToast('Copied to clipboard');
  }

  private icon(nameArg: string, sizeArg = 16): TemplateResult {
    return html`<dees-icon .icon=${`lucide:${nameArg}`} .iconSize=${sizeArg}></dees-icon>`;
  }

  private logo(sizeArg = 26): TemplateResult {
    return html`
      <svg class="logo" width=${sizeArg} height=${sizeArg} viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <path d="M16 2.5 28 9.2v13.6L16 29.5 4 22.8V9.2z" fill="currentColor" opacity="0.12"></path>
        <path d="M16 2.5 28 9.2v13.6L16 29.5 4 22.8V9.2z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"></path>
        <circle cx="16" cy="16" r="6" stroke="currentColor" stroke-width="1.6"></circle>
        <path d="M10 16h12M16 10c2.4 2 2.4 10 0 12M16 10c-2.4 2-2.4 10 0 12" stroke="currentColor" stroke-width="1.3"></path>
      </svg>
    `;
  }


  private getInitials(labelArg: string): string {
    const parts = labelArg.split(/[.\s/-]+/).filter(Boolean);
    return parts.slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || 'fg';
  }

  private getOrgAvatarUrl(orgNameArg: string): string {
    if (this.orgData?.org?.name === orgNameArg) {
      return this.orgData.org.avatarUrl;
    }
    return this.overviewData?.orgs.find((org) => org.name === orgNameArg)?.avatarUrl ?? '';
  }

  /**
   * Org-name -> avatar URL map for the activity panel. Repos carry no avatar of their own,
   * so activity rows fall back to the owning org's profile picture. Sourced from whatever is
   * currently loaded: the overview org list (covers Explore), the active org, and the active
   * repo's owner avatar (covers a deep-linked repo view).
   */
  private getOrgAvatarMap(): Record<string, string> {
    const map: Record<string, string> = {};
    for (const org of this.overviewData?.orgs ?? []) {
      if (org.avatarUrl) {
        map[org.name] = org.avatarUrl;
      }
    }
    if (this.orgData?.org?.avatarUrl) {
      map[this.orgData.org.name] = this.orgData.org.avatarUrl;
    }
    if (this.repoData?.repo?.orgAvatarUrl) {
      map[this.repoData.repo.org] = this.repoData.repo.orgAvatarUrl;
    }
    return map;
  }

  private timeAgo(timestampArg: string): string {
    if (!timestampArg) {
      return 'unknown';
    }
    const diffMs = Date.now() - new Date(timestampArg).getTime();
    if (!Number.isFinite(diffMs)) {
      return 'unknown';
    }
    const minutes = Math.max(1, Math.round(diffMs / 60000));
    if (minutes < 60) {
      return `${minutes}m ago`;
    }
    const hours = Math.round(minutes / 60);
    if (hours < 48) {
      return `${hours}h ago`;
    }
    const days = Math.round(hours / 24);
    if (days < 60) {
      return `${days}d ago`;
    }
    const months = Math.round(days / 30);
    return `${months}mo ago`;
  }

}

declare global {
  interface HTMLElementTagNameMap {
    'dotglobal-app': DotglobalApp;
  }
}
